import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { EducationalPopover } from '@liveauctioneers/hammer-ui-core/educationalPopover';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { BodyPrimary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect } from 'react';

import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

// The mounted entry-point trigger carries this id so the popover can anchor beneath it. A fixed
// id (not `useId`) is deliberate: react-tooltip's `anchorSelect` runs `querySelector`, and a
// `useId` value contains colons that are invalid in a CSS selector. The header renders once and
// the arms are mutually exclusive, so exactly one element ever carries this id.
export const SEARCH_BY_IMAGE_TRIGGER_ID = 'searchByImageTrigger';

type Props = {
    isMobile: boolean;
    onDismiss: () => void;
};

/**
 * First-exposure "NEW feature" education popover for Search by Image, anchored below whichever
 * entry-point trigger is mounted (labelled button on treatmenta, camera icon on treatmentb).
 *
 * `EducationalPopover` sits on react-tooltip, which by default behaves like a hover tooltip and
 * hides on the anchor's mouse-leave — that would dismiss the nudge when the user merely moves the
 * mouse, which the AC doesn't ask for (dismissal is X or an outside click). `imperativeModeOnly`
 * turns off react-tooltip's built-in hover / outside-click / Escape handling, so the popover stays
 * open until we unmount it, and the effect below owns the outside-click + Escape dismissal — the
 * same pattern the upload surface uses — matching the AC exactly.
 */
export const SearchByImageEducationPopover = ({ isMobile, onDismiss }: Props) => {
    const formatMessage = useAppFormatMessage();

    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            // The trigger owns its own click (it opens the uploader and dismisses the nudge), and a
            // click inside the popover shouldn't dismiss it — only a genuine outside click does.
            if (target?.closest(`#${SEARCH_BY_IMAGE_TRIGGER_ID}`) || target?.closest('.react-tooltip')) {
                return;
            }
            onDismiss();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onDismiss();
            }
        };
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onDismiss]);

    return (
        <EducationalPopover
            anchorSelect={`#${SEARCH_BY_IMAGE_TRIGGER_ID}`}
            // Figma widths: 256px on mobile, 318px tablet+; square corners (radius/container 0).
            className={isMobile ? '!w-[256px] !rounded-none' : '!w-[318px] !rounded-none'}
            imperativeModeOnly
            isInverse
            isOpen
            place="bottom"
            pages={[
                <div
                    className="flex flex-col gap-2xs"
                    key="searchByImageEducation"
                >
                    <div className="flex items-start justify-between gap-xs">
                        <div className="flex items-center gap-2xs">
                            <Badge
                                color="info"
                                emphasis="bold"
                                round
                                size="xs"
                            >
                                {formatMessage(['visualSearch', 'educationNew'])}
                            </Badge>
                            <H5 className="text-text-inverse">{formatMessage(['visualSearch', 'searchByImage'])}</H5>
                        </div>
                        <IconButton
                            aria-label={String(formatMessage(['visualSearch', 'close']))}
                            className="text-text-inverse hover:text-text-inverse"
                            hideTooltip
                            icon={Close}
                            onClick={onDismiss}
                            size="sm"
                            style="neutral"
                            type="button"
                            variant="transparent"
                        />
                    </div>
                    <BodyPrimary className="text-text-inverse">
                        {formatMessage(['visualSearch', 'educationBody'])}
                    </BodyPrimary>
                </div>,
            ]}
        />
    );
};
